import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicHeader, ArabicFooter } from "./ArabicHome";

const CART_STORAGE_KEY = "tarahumDonationCart";

const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getStoredCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const items = stored ? JSON.parse(stored) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const saveCart = (items) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

const getQuantity = (item) => {
  const quantity = Number(item?.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
};

export default function Cart({ language = "en" }) {
  const interactions = useHomeInteractions();
  const [cartItems, setCartItems] = useState(() => (
    getStoredCart().map((item) => ({ ...item, quantity: getQuantity(item) }))
  ));

  useEffect(() => {
    document.title = "Cart | Tarahum Charity Foundation";
  }, []);

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [language]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (parseAmount(item.amount) * getQuantity(item)), 0),
    [cartItems]
  );

  const totalQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + getQuantity(item), 0),
    [cartItems]
  );

  const updateItemQuantity = (id, quantity) => {
    const normalizedQuantity = Math.max(1, Math.floor(Number(quantity) || 1));
    const nextItems = cartItems.map((item) => (
      item.id === id ? { ...item, quantity: normalizedQuantity } : item
    ));
    setCartItems(nextItems);
    saveCart(nextItems);
  };

  const removeItem = (id) => {
    const nextItems = cartItems.filter((item) => item.id !== id);
    setCartItems(nextItems);
    saveCart(nextItems);
  };

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{language === "ar" ? "سلة التبرعات" : "Cart"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{"home"}</Link></li>
              <li>{"/"}</li>
              <li>{language === "ar" ? "سلة التبرعات" : "Cart"}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="cart-wrap-1">
        <div className="container">
          <div className="cart-container">
            <div className="cart-head">
              <span>
                <i className="fa-solid fa-cart-shopping"></i>
                {language === "ar" ? "سلة التبرعات" : " Donation Cart"}
              </span>
              <h2>{language === "ar" ? "راجع تبرعك" : "Review Your Donation"}</h2>
              <p>{language === "ar" ? "أكد تفاصيل تبرعك قبل المتابعة إلى إتمام التبرع." : "Confirm your donation details before proceeding to checkout."}</p>
            </div>

            <div className="cart-layout">
              <div className="cart-items">
                {cartItems.length ? cartItems.map((item) => (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-icon">
                      <i className="fa-solid fa-hand-holding-heart"></i>
                    </div>

                    <div className="cart-info">
                      <h3>{item.title}</h3>
                      <p>
                        {item.typeLabel || ""}
                        {item.caseNo ? ` | Case no ${item.caseNo}` : ""}
                      </p>
                    </div>

                    <div className="cart-qty">
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(item.id, getQuantity(item) - 1)}
                        aria-label="Decrease quantity"
                      >
                        {"-"}
                      </button>
                      <input
                        type="number"
                        value={getQuantity(item)}
                        min="1"
                        onChange={(event) => updateItemQuantity(item.id, event.target.value)}
                        aria-label="Donation quantity"
                      />
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(item.id, getQuantity(item) + 1)}
                        aria-label="Increase quantity"
                      >
                        {"+"}
                      </button>
                    </div>

                    <div className="cart-price">
                      <strong>{formatAmount(parseAmount(item.amount) * getQuantity(item))}</strong>
                      <span>{"AED"}</span>
                    </div>

                    <button className="cart-delete" type="button" onClick={() => removeItem(item.id)}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                )) : (
                  <div className="cart-item">
                    <div className="cart-icon">
                      <i className="fa-solid fa-cart-shopping"></i>
                    </div>
                    <div className="cart-info">
                      <h3>{language === "ar" ? "سلة التبرعات فارغة" : "Your donation cart is empty"}</h3>
                      <p>{"Add a critical case from the home page to continue."}</p>
                    </div>
                    <Link to="/" className="cart-btn cart-btn-dark">
                      {"Browse Cases"}
                    </Link>
                  </div>
                )}
              </div>

              <aside className="cart-summary">
                <div className="summary-glow"></div>
                <div className="summary-glow two"></div>

                <div className="summary-title">
                  <span>{language === "ar" ? "ملخص السلة" : "Cart Summary"}</span>
                  <i className="fa-solid fa-receipt"></i>
                </div>

                <div className="summary-row">
                  <span>{"Subtotal"}</span>
                  <strong>{formatAmount(subtotal)}{" AED"}</strong>
                </div>

                <div className="summary-row">
                  <span>{"Items"}</span>
                  <strong>{totalQuantity}</strong>
                </div>

                <div className="summary-total">
                  <span>{"Total"}</span>
                  <strong>{formatAmount(subtotal)}{" "}<small>{"AED"}</small></strong>
                </div>

                <div className="summary-actions">
                  <a href="login.html" className="cart-btn cart-btn-light">
                    <i className="fa-solid fa-user"></i>
                    {language === "ar" ? " تسجيل الدخول" : " Login"}
                  </a>

                  <Link
                    to={language === "ar" ? "/ar/checkout" : "/checkout"}
                    className={`cart-btn cart-btn-dark${cartItems.length ? "" : " disabled"}`}
                    aria-disabled={!cartItems.length}
                    onClick={(event) => { if (!cartItems.length) event.preventDefault(); }}
                  >
                    {language === "ar" ? "إتمام التبرع" : "Checkout"}
                    <i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>

                <div className="payment-partners">
                  <h4>{language === "ar" ? "شركاء الدفع" : "Payment Partners"}</h4>
                  <div className="payment-logos">
                    <img src="/images/img/payments.jpg" style={{ borderRadius: 10, overflow: "hidden" }} alt="Payment partners" />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      {language === "ar" ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
